import styles from "./lead-form.module.scss";
import Select from "react-select";
import { ReactComponent as LeftIcon } from "assets/caretleft.svg";
import { useEffect, useState } from "react";
import { phoneMask } from "helpers/masks";
import { isValidEmail, isValidPhone } from "helpers/validators";
import useExternalApiService from "helpers/ExternalApiService";
import { store } from "stores/Store";
import { useNavigate } from "react-router-dom";
import { splitFirstAndLastName } from "helpers";
import ConfirmationModal from "components/ConfirmationModal/ConfirmationModal";
import { Button, CheckIcon } from "@mantine/core";
import GeoValidationDrawer from "./components/GeoValidationDrawer";
import { logEvent } from '@amplitude/analytics-browser';

type View = "submit" | "draft" | "lead" | "backedit";

const MOCK_SPECIALTIES = [
  "RWS",
  "BCHN",
  "BS in Exercise Kinesiology",
  "BS Nutrition",
  "CCN",
  "CNC",
  "CNS",
  "CTNC",
  "DC",
  "Dentist",
  "DO",
  "DOM",
  "DSS-Health Coach",
  "DSS-RN",
  "DVM",
  "International",
  "LAc",
  "LPC/LCSW",
  "MD",
  "Midwife",
  "MS Nutrition",
  "NANP Professional",
  "ND",
  "NTP",
  "Nurse Practitioner",
  "OD",
  "Other",
  "PA",
  "Pharmacist",
  "PhD/PsyD",
  "PT/DPT",
  "RD",
  "RN"
];

const STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const MOCK_PRACTICE_TYPE = [
  "Dental office",
  "Educational Organization",
  "Fitness",
  "Hospital",
  "Med Spa / Aesthetics",
  "Online Platform",
  "Other",
  "Pharmacy",
  "Private Practice",
  "Sports Team",
  "Students"
];

interface PractionerLeadForm {
  name: string;
  phone: string;
  specialtyType: string;
  email: string;
  practiceType: string;
  status: string;
  primary_name: string;
  fmc_email: string;
  firstname: string;
  lastname: string;
  companyname: string;
  address1: string;
  address2: string;
  city: string;
  state: any; // Changed from string to any to accommodate react-select object
  zipcode: string;
  readyToPurchase: boolean;
  latLon?: [number, number];
}

const getSplittedLineAddress = (n = "") => {
  if (!n) {
    return ["", ""];
  }
  const result = n.split("\n");
  if (result && result.length === 2) {
    return [result[0], result[1]];
  }
  if (result && result.length === 1) {
    return [result[0], ""];
  }
  return ["", ""];
}

type GeoValidationAction = "validate" | "draft" | "";

interface Props {
  practitionerInfo?: any;
  onNavigate: (v: View) => void;
}
const LeadForm: React.FC<Props> = ({ practitionerInfo, onNavigate }) => {


  const [_firstname, _lastname] = splitFirstAndLastName(practitionerInfo?.name || practitionerInfo?.place.name);
  const [_address1, _address2] = getSplittedLineAddress(practitionerInfo?.address);
  const stateFromPlaces = practitionerInfo?.place?.address_components?.find((a: any) => a?.types?.includes("administrative_area_level_1"))?.short_name;

  const address1FromPlaces = (practitionerInfo?.place?.address_components?.find((a: any) => a?.types?.includes("street_number"))?.long_name || "") + " " + (practitionerInfo?.place?.address_components?.find((a: any) => a?.types?.includes("route"))?.long_name || "");
  const address2FromPlaces = practitionerInfo?.place?.address_components?.find((a: any) => a?.types?.includes("subpremise"))?.long_name || ""

  const [form, setForm] = useState<PractionerLeadForm>({
    phone: practitionerInfo?.place?.formatted_phone_number || practitionerInfo?.phone || "",
    name: practitionerInfo?.name || `${_firstname} ${_lastname}` || "",
    primary_name: practitionerInfo?.name || `${_firstname} ${_lastname}` || "",
    specialtyType: practitionerInfo?.specialty ?
      { label: practitionerInfo?.specialty, value: practitionerInfo?.specialty } as any :
      practitionerInfo?.place?.types[0],
    practiceType: "",
    email: practitionerInfo?.email || "",
    status: "LEAD-INCOMPLETE",
    fmc_email: store.impersonatedUser || "",
    firstname: practitionerInfo?.firstname || _firstname,
    lastname: practitionerInfo?.lastname || _lastname,
    companyname: practitionerInfo?.companyname || practitionerInfo?.place?.name || "",
    city: practitionerInfo?.city || practitionerInfo?.place?.address_components?.find((a: any) => a?.types?.includes("locality"))?.long_name,
    state: practitionerInfo?.state ?
      { label: practitionerInfo?.state, value: practitionerInfo?.state } as any :
      { label: stateFromPlaces || "", value: stateFromPlaces || "" } as any,
    zipcode: practitionerInfo?.zipcode || practitionerInfo?.place?.address_components?.find((a: any) => a?.types?.includes("postal_code"))?.long_name,
    address1: _address1 || address1FromPlaces,
    address2: _address2 || address2FromPlaces,
    readyToPurchase: Boolean(practitionerInfo?.ready_to_purchase),
    latLon: (practitionerInfo?.latitude && practitionerInfo?.longitude) 
      ? [practitionerInfo?.latitude, practitionerInfo?.longitude]
      : undefined,
  });
  const [formState, setFormState] = useState<Partial<PractionerLeadForm>>({});

  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { putData, postData } = useExternalApiService(apiUrl);
  const navigate = useNavigate();
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const isValid = Object.keys(formState).length === 0;
  const [geoValidationAction, setGeoValidationAction] = useState<GeoValidationAction>("");

  useEffect(() => {
    if (alreadyConfirmed) {
      submitLead(false);
    }
  }, [alreadyConfirmed]);

  useEffect(() => {
    const errObj: { [k: string]: string } = {};

    if (!form.lastname.trim()) {
      errObj["lastname"] = "empty";
    }

    if (!form.firstname.trim()) {
      errObj["firstname"] = "empty";
    }

    if (!form.address1) {
      errObj["address1"] = "empty";
    }
    /*
    if (!form.specialtyType) {
      errObj["specialtyType"] = "empty";
    }
    if (!form.practiceType) {
      errObj["practiceType"] = "empty";
    }
    */
    if (!isValidEmail(form.email)) {
      console.log("!isValidEmail=", !isValidEmail(form.email));
      errObj["email"] = "invalid";
    }

    if (!isValidPhone(form.phone)) {
      errObj["phone"] = "invalid";
    }

    if (!isGeoValidated) {
      errObj["address1"] = "invalid";
      console.log("practitionerInfo", practitionerInfo)
    }
    setFormState(errObj);
  }, [form]);

  const handleGeoValidation = (action: GeoValidationAction = "validate") => {
    setGeoValidationAction(action);
  };

  const submitLead = async (isDraft: boolean = false, f?: PractionerLeadForm) => {
    if (!isDraft && !alreadyConfirmed) {
      setIsConfirmationModalOpen(true);
      return;
    }
    if (!f) {
      f = form;
    }
    try {
      if (practitionerInfo?.id) {
        const _status = !isDraft ? "LEAD-AWAITING-NETSUITE" :
          (isValid ? 'LEAD-READY2SUBMIT' : 'LEAD-INCOMPLETE');
        const result = await putData(`/api/add_practitioner_to_chat/${practitionerInfo.id}`, {
          practitioner_data: {
            address: `${f.address1}\n${f.address2}`,
            address1: `${f.address1}`,
            address2: `${f.address2}`,
            phone: f.phone,
            name: `${f.firstname} ${f.lastname}`,
            primary_name: `${f.firstname} ${f.lastname}`,
            firstname: f.firstname,
            lastname: f.lastname,
            companyname: f.companyname,
            specialty: (f.specialtyType as any)?.value,
            practice_type: (f.practiceType as any)?.value,
            email: f.email,
            status: _status,
            data_source: "SALES ASSISTANT",
            fmc_email: store.impersonatedUser,
            latitude: practitionerInfo?.place?.geometry.location.lat || (f?.latLon ? f.latLon[0] : undefined),
            longitude: practitionerInfo?.place?.geometry.location.lng || (f?.latLon ? f.latLon[1] : undefined),
            city: f.city,
            state: (f.state as any)?.value,
            zipcode: f.zipcode,
            fmc: store.impersonatedUserName,
            fmc_netsuite_id: store.impersonatedUserNetsuiteID,
            rsd: store.impersonatedUserRsdName,
            rsd_email: store.impersonatedUserRsdEmail,
            stage: "LEAD",
            ready_to_purchase: f.readyToPurchase,
          }
        });
      } else {
        const _status = !isDraft ? "LEAD-AWAITING-NETSUITE" :
          (isValid ? 'LEAD-READY2SUBMIT' : 'LEAD-INCOMPLETE');

        const result = await postData('/api/add_practitioner_to_chat', {
          practitioner_data: {
            address: `${f.address1}\n${f.address2}`,
            address1: `${f.address1}`,
            address2: `${f.address2}`,
            phone: f.phone,
            name: `${f.firstname} ${f.lastname}`,
            primary_name: `${f.firstname} ${f.lastname}`,
            firstname: f.firstname,
            lastname: f.lastname,
            companyname: f.companyname,
            specialty: (f.specialtyType as any)?.value,
            practice_type: (f.practiceType as any)?.value,
            email: f.email,
            status: _status,
            data_source: "SALES ASSISTANT",
            fmc_email: store.impersonatedUser,
            latitude: practitionerInfo?.place?.geometry.location.lat || (f?.latLon ? f.latLon[0] : undefined),
            longitude: practitionerInfo?.place?.geometry.location.lng || (f?.latLon ? f.latLon[1] : undefined),
            city: f.city,
            state: (f.state as any)?.value,
            zipcode: f.zipcode,
            fmc: store.impersonatedUserName,
            fmc_netsuite_id: store.impersonatedUserNetsuiteID,
            rsd: store.impersonatedUserRsdName,
            rsd_email: store.impersonatedUserRsdEmail,
            stage: "LEAD",
            ready_to_purchase: f.readyToPurchase,
          }
        });
      }
      store.setPractitionersCarouselFilter("Geo-Verified");
      store.clearPractitionersCache();
      store.clearLeadsCache();
      const netsuiteLeadStr = "Sent Lead to Netsuite" + f.firstname + " " + f.lastname;
      trackClick(netsuiteLeadStr);
      navigate(`/?${new URLSearchParams({prompt: 'practitioners', toggle: "false", targetSubTab: "lead"}).toString()}`)
    } catch (error) {
      console.error(error);
    }
  }

  const isGeoValidated = (
    (practitionerInfo?.place?.geometry.location.lat && practitionerInfo?.place?.geometry.location.lng) ||
    (form?.latLon?.length === 2)
  );

  const trackClick = (buttonName: string) => {
          const label = buttonName
          logEvent(label, { buttonName });
  };

  useEffect(() => {
    if (practitionerInfo?.ready_to_purchase != null) {
      setForm(f => ({
        ...f,
        readyToPurchase: Boolean(practitionerInfo.ready_to_purchase)
      }));
    }
  }, [practitionerInfo?.ready_to_purchase]);

  return (
    <div className={!isValid ? styles["wrapper"] : `${styles["wrapper"]} ${styles["wrapper--success"]}`}>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={() => {
          setIsConfirmationModalOpen(false);
          setAlreadyConfirmed(true);
        }}
        message="Once you send to Netsuite, you will not be able to edit details. Please make sure details are correct:"
        customRender={
          <div className={styles["confirmation-content"]}>
            <h3>Email</h3>
            <span>{form.email}</span>

            <h3>Address</h3>
            <span>{form.address1} </span>
            <span>{form.address2}</span>
            <span>{form.city}, {form.state.label} {form.zipcode}</span>

            <h3>Lat/Long</h3>
            <h3>(Required for Verified Check-Ins)</h3>
            {practitionerInfo?.place?.geometry.location.lat && practitionerInfo?.place?.geometry.location.lng && (
              <span>{practitionerInfo?.place?.geometry.location.lat}, {practitionerInfo?.place?.geometry.location.lng}</span>
            )}
            {practitionerInfo?.latitude && practitionerInfo?.longitude && (
              <span>{practitionerInfo?.latitude}, {practitionerInfo?.longitude}</span>
            )}
          </div>}
      />
      <GeoValidationDrawer
        opened={!!geoValidationAction}
        onClose={() => setGeoValidationAction("")}
        addressToValidate={{
          administrativeArea: (form.state as any)?.value || "",
          locality: form.city || "",
          postalCode: form.zipcode || "",
          addressLines: [form.address1 || "", form.address2 || ""],
        }}
        onConfirm={(a) => {
          if (a) {
            const f = {
              ...form,
              state: { label: a.administrativeArea, value: a.administrativeArea },
              city: a.locality,
              address1: a.addressLines[0],
              address2: a.addressLines[1],
              zipcode: a.postalCode,
              latLon: a.latLon,
            };
            setForm(f);
            if (geoValidationAction === "draft") {
              submitLead(true, f);
            }
          } else {
            const f = {
              ...form,
              latLon: undefined
            };
            setForm(f);
            if (geoValidationAction === "draft") {
              submitLead(true, f);
            }
          }
          setGeoValidationAction("");
        }}
      />

      <div className={!isValid ? styles["border-container"] : `${styles["border-container"]} ${styles["border-container--success"]}`}>
        {isValid ?
          (<span>Not in Netsuite: Ready to Submit</span>) :
          (<span>Not in Netsuite: Incomplete Lead</span>)
        }
      </div>
      <div className={!isValid ? styles["border-container-left"] : `${styles["border-container-left"]} ${styles["border-container-left--success"]}`}>
      </div>
      <div className={!isValid ? styles["border-container-right"] : `${styles["border-container-right"]} ${styles["border-container-right--success"]}`}>
      </div>
      <LeftIcon onClick={() => onNavigate(practitionerInfo?.id ? "backedit" : "lead")} className={styles.leftIcon} />
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles["form-header"]}>
            <h3>Create a New Lead</h3>
          </div>
          {/*<h2 className={styles["practitioner-name"]}>{practitionerInfo.place.name}</h2>*/}
          <form className={styles["form"]}>
            <div className={styles["form__side-by-side"]}>
              <div className={styles["form__input"]}>
                <label>
                  First Name<span className={styles.required}>*</span>
                </label>
                <input
                  className={formState.firstname ? styles["form__input--error"] : ""}
                  value={form.firstname}
                  onChange={(e) => setForm({ ...form, firstname: e.target.value })}
                  placeholder="Pamela"
                  type="text"
                />
              </div>
              <div className={styles["form__input"]}>
                <label>
                  Last Name<span className={styles.required}>*</span>
                </label>
                <input
                  className={formState.lastname ? styles["form__input--error"] : ""}
                  value={form.lastname}
                  onChange={(e) => setForm({ ...form, lastname: e.target.value })}
                  placeholder="Practitioner"
                  type="text"
                />
              </div>
            </div>
            <div className={styles["form__input"]}>
              <div className={styles["form__input-header"]}>
                <label className={styles["form__input-label"]}>
                  Company Name
                </label>
                <label className={styles["form__checkbox-label"]}>
                  <input
                    type="checkbox"
                    checked={form.readyToPurchase}
                    onChange={e => setForm({ ...form, readyToPurchase: e.target.checked })}
                  />
                  Ready to Purchase
                </label>
              </div>
              <input
                value={form.companyname}
                onChange={(e) => setForm({ ...form, companyname: e.target.value })}
                placeholder="Company Name"
                type="text"
              />
            </div>
            <div className={styles["form__input"]}>
              <div className={styles["form__input-header"]}>
                <label className={styles["form__input-label"]}>
                  Address<span className={styles.required}>*</span>
                </label>
                <label className={styles["form__checkbox-label"]}>
                  <Button
                    onClick={() => handleGeoValidation()}
                    leftSection={isGeoValidated ? <CheckIcon size={10} /> : null}
                    variant="transparent">
                    Geo-Validate
                  </Button>
                </label>
              </div>
              <input
                className={formState.address1 ? styles["form__input--error"] : ""}
                value={form.address1}
                placeholder="123 Wellness Road"
                onChange={(e) => setForm({ ...form, address1: e.target.value })}
                type="text" />
              <input
                className={formState.address2 ? styles["form__input--error"] : ""}
                value={form.address2}
                onChange={(e) => setForm({ ...form, address2: e.target.value })}
                placeholder="Suite 456"
                type="text" />
            </div>
            <div className={styles["form__input"]}>
              <label>
                City<span className={styles.required}>*</span>
              </label>
              <input
                className={formState.city ? styles["form__input--error"] : ""}
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="Palm Coast"
                type="text" />
            </div>
            <div className={styles["form__side-by-side"]}>
              <div className={styles["form__input"]}>
                <label>
                  State<span className={styles.required}>*</span>
                </label>
                <Select
                  value={form.state}
                  className={styles["form__input--select"]}
                  onChange={(v: any) => setForm({ ...form, state: v })}
                  placeholder="FL"
                  menuPlacement="top"
                  styles={{
                    control: (baseStyles: any) => ({
                      ...baseStyles,
                      border: 'none',
                      borderBottom: '1px solid #ccc',
                      paddingLeft: 0,
                      marginLeft: 0,
                      color: '#4F7D9C',
                      boxShadow: 'none',
                      minHeight: "30px",
                      maxHeight: "30px",
                      '&:hover': {
                        boxShadow: 'none'
                      },

                    }),
                    dropdownIndicator: (baseStyles: any) => ({
                      ...baseStyles,
                      marginTop: "-5px",
                    }),
                    valueContainer: (baseStyles: any) => ({
                      ...baseStyles,
                      paddingLeft: 0,
                      marginLeft: 0,
                    }),
                    indicatorSeparator: () => ({
                      display: 'none'
                    })
                  }}
                  options={STATES.map((s) => ({
                    label: s,
                    value: s,
                  })) as []} />
              </div>
              <div className={styles["form__input"]}>
                <label>
                  Zip-Code<span className={styles.required}>*</span>
                </label>
                <input
                  className={formState.zipcode ? styles["form__input--error"] : ""}
                  value={form.zipcode}
                  onChange={(e) => setForm({ ...form, zipcode: e.target.value })}
                  placeholder="01234"
                  type="text"
                />
              </div>
            </div>
            <div className={styles["form__input"]}>
              <label>
                Phone<span className={styles.required}>*</span>
              </label>
              <input
                className={formState.phone ? styles["form__input--error"] : ""}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: phoneMask(e.target.value) })}
                placeholder="(888)888-8888"
                type="text" />
            </div>
            <div className={styles["form__input"]}>
              <label>
                Email<span className={styles.required}>*</span>
              </label>
              <input
                className={formState.email ? styles["form__input--error"] : ""}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="email@practitioner.com"
                type="text" />
            </div>
            <div className={styles["form__input"]}>
              <label>
                Speciality Type
              </label>
              <Select
                value={form.specialtyType}
                onChange={(v: any) => setForm({ ...form, specialtyType: v })}
                placeholder="Choose Practitioner Specialty (Optional)"
                menuPlacement="top"
                styles={{
                  control: (baseStyles: any) => ({
                    ...baseStyles,
                    border: 'none',
                    borderBottom: '1px solid #ccc',
                    paddingLeft: 0,
                    marginLeft: 0,
                    color: '#4F7D9C',
                    boxShadow: 'none',
                    '&:hover': {
                      boxShadow: 'none'
                    }
                  }),
                  valueContainer: (baseStyles: any) => ({
                    ...baseStyles,
                    paddingLeft: 0,
                    marginLeft: 0,
                  }),
                  indicatorSeparator: () => ({
                    display: 'none'
                  })
                }}
                options={MOCK_SPECIALTIES.map((sp) => ({
                  label: sp,
                  value: sp,
                })) as []} />
            </div>
            <div className={styles["form__input"]}>
              <label>
                Practice Type
              </label>
              <Select
                value={form.practiceType}
                onChange={(v: any) => setForm({ ...form, practiceType: v })}
                placeholder="Choose Practice Type (Optional)"
                menuPlacement="top"
                styles={{
                  control: (baseStyles: any) => ({
                    ...baseStyles,
                    border: 'none',
                    borderBottom: '1px solid #ccc',
                    paddingLeft: 0,
                    marginLeft: 0,
                    color: '#4F7D9C',
                    boxShadow: 'none',
                    '&:hover': {
                      boxShadow: 'none'
                    }
                  }),
                  valueContainer: (baseStyles: any) => ({
                    ...baseStyles,
                    paddingLeft: 0,
                    marginLeft: 0,
                  }),
                  indicatorSeparator: () => ({
                    display: 'none'
                  })
                }}
                options={MOCK_PRACTICE_TYPE.map((pt) => ({
                  label: pt,
                  value: pt,
                })) as []} />
            </div>
            <div className={styles["buttons-container"]}>
              <button
                disabled={!isValid}
                onClick={() => submitLead(false)}
                className={styles["button"]}
                type="button">
                Send to Netsuite
                <span>CREATE LEAD</span>
              </button>
              <button
                className={styles["draft-button"]}
                onClick={() => {
                  const leadstr = "Saved lead draft: " + form.firstname + form.lastname;
                  trackClick(leadstr);
                  if (isGeoValidated) {
                    // already have valid lat/lng → skip the drawer and save draft immediately
                    submitLead(true);
                  } else {
                    // not yet validated → open the drawer as before
                    handleGeoValidation("draft");
                  }                  
                }}
                type="button">
                Save Draft
                <span>IN SALES ASSISTANT</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

  );
};

export default LeadForm;